/**
 * Store Types
 *
 * TypeScript types for store management in admin dashboard.
 */

// ============================================================================
// ENUMS
// ============================================================================

export type StoreStatus =
  | 'PENDING_APPROVAL'
  | 'CONFIGURING'
  | 'ACTIVE'
  | 'SUSPENDED'
  | 'CLOSED'
  | 'TEMPORARILY_CLOSED';

export const STORE_STATUS_LABELS: Record<StoreStatus, string> = {
  PENDING_APPROVAL: 'Pendiente aprobación',
  CONFIGURING: 'Configurando',
  ACTIVE: 'Activa',
  SUSPENDED: 'Suspendida',
  CLOSED: 'Cerrada',
  TEMPORARILY_CLOSED: 'Cerrada temporalmente',
};

export const STORE_STATUS_COLORS: Record<StoreStatus, 'warning' | 'info' | 'success' | 'error' | 'default'> = {
  PENDING_APPROVAL: 'warning',
  CONFIGURING: 'info',
  ACTIVE: 'success',
  SUSPENDED: 'error',
  CLOSED: 'default',
  TEMPORARILY_CLOSED: 'warning',
};

// ============================================================================
// INTERFACES
// ============================================================================

export interface StoreManager {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
}

/**
 * Persona del equipo de una sucursal — owner de la compañía o
 * manager de la tienda. Devuelta por
 * `GET /companies/:companyId/stores/:storeId/team`.
 */
export interface StoreTeamMember {
  userId: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  avatarUrl: string | null;
  /** ISO string. `joinedAt` del CompanyUser para owner; del StoreUser para manager. */
  joinedAt: string | null;
}

/** Respuesta del endpoint de equipo de sucursal. Cualquiera de los dos puede ser null. */
export interface StoreTeam {
  owner: StoreTeamMember | null;
  manager: StoreTeamMember | null;
}

export interface StoreCity {
  id: string;
  name: string;
  province?: {
    id: string;
    name: string;
    code?: string | null;
    /** Hidratado por el detail endpoint cuando viene anidado. */
    country?: {
      id: string;
      code?: string | null;
      name?: string | null;
    };
  };
}

export interface Store {
  id: string;
  companyId: string;
  name: string;
  slug: string;
  code?: string | null;
  address: string;
  addressLine2?: string | null;
  phone: string;
  email?: string | null;
  status: StoreStatus;
  isOpen: boolean;
  serviceTypes?: string[];
  latitude?: number | null;
  longitude?: number | null;
  postalCode?: string | null;
  createdAt: string;
  city: StoreCity;
  manager?: StoreManager | null;
  /** El detail endpoint resuelve también country y company; el listado
   *  los omite. Por eso van ambos como opcionales. */
  country?: {
    id: string;
    code?: string | null;
    name?: string | null;
  };
  company?: {
    id: string;
    name: string;
    slug?: string;
  };
}

export interface StoreMedia {
  id: string;
  fileName: string;
  originalName: string;
  mimeType: string;
  size: number;
  type: 'IMAGE' | 'DOCUMENT';
  bucket: string;
  path: string;
  url: string;
  storeId: string;
  uploadedById: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  sortOrder: number;
  isPrimary: boolean;
}

// ============================================================================
// SERVICE TYPES
// ============================================================================

export type ServiceType =
  | 'QUEUE'
  | 'TABLE'
  | 'DELIVERY'
  | 'SUBSCRIPTION'
  | 'BOOKING'
  | 'APPOINTMENT';

export const SERVICE_TYPE_OPTIONS: { value: ServiceType; label: string; description: string }[] = [
  { value: 'QUEUE', label: 'Pedidos para recoger', description: 'Clientes hacen pedido y recogen cuando está listo' },
  { value: 'TABLE', label: 'Pedidos en mesa', description: 'Clientes ordenan desde su mesa escaneando un QR' },
  { value: 'DELIVERY', label: 'Envío a domicilio', description: 'Envío de pedidos directamente al cliente' },
  { value: 'SUBSCRIPTION', label: 'Membresías', description: 'Planes con cobro recurrente' },
  { value: 'BOOKING', label: 'Reservas', description: 'Reserva de espacios o recursos por horario' },
  { value: 'APPOINTMENT', label: 'Citas', description: 'Agendamiento de citas con un profesional' },
];

// ============================================================================
// DTOs
// ============================================================================

export interface StoreQueryParams {
  page?: number;
  limit?: number;
  status?: StoreStatus;
  /** Filtrar por país (a través de la empresa) — soportado por el admin list. */
  countryId?: string;
  cityId?: string;
  isOpen?: boolean;
  acceptsPickup?: boolean;
  acceptsDelivery?: boolean;
  search?: string;
}

export interface ApproveStoreDto {
  notes?: string;
}

export interface SuspendStoreDto {
  reason: string;
  internalNotes?: string;
}

export interface CloseStoreDto {
  reason: string;
  internalNotes?: string;
}

export interface TemporaryCloseStoreDto {
  reason: string;
  estimatedReopenDate?: string;
  internalNotes?: string;
}

export interface ReactivateStoreDto {
  notes?: string;
}

export interface CreateStoreDto {
  name: string;
  cityId: string;
  address: string;
  phone: string;
  latitude: number;
  longitude: number;
  code?: string;
  description?: string;
  addressLine2?: string;
  neighborhood?: string;
  postalCode?: string;
  email?: string;
  timezone?: string;
  serviceTypes?: ServiceType[];
  acceptsPickup?: boolean;
  acceptsDelivery?: boolean;
  deliveryRadius?: number;
}

export interface UpdateStoreDto {
  name?: string;
  code?: string;
  description?: string;
  phone?: string;
  email?: string;
  address?: string;
  addressLine2?: string;
  neighborhood?: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
  cityId?: string;
  documentMediaIds?: string[];
}

// ============================================================================
// API RESPONSE
// ============================================================================

export interface PaginatedStores {
  data: Store[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}
